import { useEffect } from 'react';
import styled from 'styled-components';

import { colors } from 'constants/colors';
import {
    GetMicrostoreSiteQuery,
    MicrostoreColumnGroupSetupFragmentFragment,
    MicrostoreColumnSetupFragmentFragment,
    SiteSetupFragmentFragment,
} from 'data/__generated__';

// Flat list of every column that needed to be set up, rendered as buttons at the top of the
// "Détail du rangement" section. Selecting a button drives the column details shown below.
export const ColumnSetupSelection = ({
    site,
    siteSetup,
    selectedColumnSetup,
    setSelectedColumnGroupSetup,
    setSelectedColumnSetup,
}: {
    site: GetMicrostoreSiteQuery['siteWithLocationInfo'];
    siteSetup: SiteSetupFragmentFragment;
    selectedColumnSetup: MicrostoreColumnSetupFragmentFragment | null;
    setSelectedColumnGroupSetup: (groupSetup: MicrostoreColumnGroupSetupFragmentFragment | null) => void;
    setSelectedColumnSetup: (columnSetup: MicrostoreColumnSetupFragmentFragment | null) => void;
}) => {
    const hasMultipleGroups = siteSetup.microstoreColumnGroupSetups.length > 1;

    const entries = siteSetup.microstoreColumnGroupSetups
        .flatMap((groupSetup) => {
            const microstoreColumnGroup = site.microstoreColumnGroups.find(
                ({ _id }) => _id === groupSetup.microstoreColumnGroupId,
            );
            return groupSetup.microstoreColumnSetups.map((columnSetup) => {
                const microstoreColumn = microstoreColumnGroup?.columns.find(
                    ({ _id }) => _id === columnSetup.microstoreColumnId,
                );
                return {
                    key: columnSetup._id,
                    groupSetup,
                    columnSetup,
                    groupName: microstoreColumnGroup?.name ?? '',
                    groupPosition: microstoreColumnGroup?.positionInSite ?? 0,
                    columnName: microstoreColumn?.name ?? columnSetup.microstoreColumnId,
                    columnPosition: microstoreColumn?.positionInColumnGroup ?? 0,
                };
            });
        })
        .sort((a, b) => a.groupPosition - b.groupPosition || a.columnPosition - b.columnPosition);

    // Keep the selected setup in sync when the siteSetup is refreshed (subscription updates).
    useEffect(() => {
        if (!selectedColumnSetup) return;
        const match = entries.find(({ columnSetup }) => columnSetup._id === selectedColumnSetup._id);
        if (match && (match.columnSetup !== selectedColumnSetup || match.groupSetup)) {
            setSelectedColumnGroupSetup(match.groupSetup);
            if (match.columnSetup !== selectedColumnSetup) {
                setSelectedColumnSetup(match.columnSetup);
            }
        }
    }, [siteSetup]);

    return (
        <Container>
            {entries.map(({ key, groupSetup, columnSetup, groupName, columnName }) => (
                <ColumnButton
                    key={key}
                    onClick={() => {
                        setSelectedColumnGroupSetup(groupSetup);
                        setSelectedColumnSetup(columnSetup);
                    }}
                    $isSelected={selectedColumnSetup?._id === columnSetup._id}
                >
                    {hasMultipleGroups ? `${groupName} · ${columnName}` : columnName}
                </ColumnButton>
            ))}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 15px;
`;

const ColumnButton = styled.div<{ $isSelected: boolean }>`
    cursor: pointer;
    padding: 6px 12px;
    border: 1px solid ${colors.pureBlack};
    border-radius: 8px;
    background-color: ${({ $isSelected }) => ($isSelected ? colors.greenDarker : colors.pureWhite)};
    color: ${({ $isSelected, theme }) => ($isSelected ? colors.pureWhite : theme.textColor)};
    font-weight: ${({ $isSelected }) => ($isSelected ? 'bold' : 'normal')};
    white-space: nowrap;
`;
